"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const fmko_ts_common_1 = require("fmko-ts-common");
const RenderingContext_1 = tslib_1.__importDefault(require("./renderers/RenderingContext"));
const SortDirection_1 = require("./model/SortDirection");
const GridConstants_1 = tslib_1.__importDefault(require("./GridConstants"));
const GridColumnDataDisplayedType_1 = require("./GridColumnDataDisplayedType");
const fmko_ts_widgets_1 = require("fmko-ts-widgets");
const HiddenTableHeader_1 = tslib_1.__importDefault(require("./model/HiddenTableHeader"));
class ImprovedHTMLTable extends fmko_ts_common_1.Widget {
    constructor() {
        super();
        this.columnHeaders = [];
        this.filterHeaders = [];
        this.columnStyles = [];
        this.columnFooters = {};
        this.fixedRowStyle = [];
        this.element = document.createElement("div");
        this.table = this.renderTable();
        this.element.appendChild(this.table);
    }
    addClickHandler(handler) {
        this.element.addEventListener("click", handler);
    }
    setGridLayout(gridLayout) {
        this.gridLayout = gridLayout;
    }
    getGridLayout() {
        return this.gridLayout;
    }
    setCaption(caption) {
        this.caption = caption;
        const captionElement = this.table?.caption;
        if (captionElement) {
            captionElement.textContent = this.caption;
        }
    }
    setSummary(summary) {
        this.summary = summary;
    }
    addHeader(heading = "", style, filterHeader) {
        this.columnHeaders.push(heading);
        if (style) {
            this.columnStyles.push([...style]);
        }
        if (filterHeader) {
            this.filterHeaders.push(filterHeader);
        }
    }
    getFilterHeaders() {
        return this.filterHeaders;
    }
    addFooter(footer, row) {
        if (!this.columnFooters[row]) {
            this.columnFooters[row] = [];
        }
        this.columnFooters[row].push(footer);
    }
    render() {
        // Don't observe while we rebuild the table
        const renderingContext = new RenderingContext_1.default();
        this.thead = this.renderHeader(renderingContext);
        this.tbody = this.renderRows(renderingContext);
        this.tfoot = this.renderFooters(renderingContext);
        this.table.appendChild(this.thead);
        this.table.appendChild(this.tbody);
        if (Object.keys(this.columnFooters).length) {
            this.table.appendChild(this.tfoot);
        }
        const cellToWidget = renderingContext.getCell2Widget();
        const headerWidgets = renderingContext.getHeaderWidget();
        const footerWidgets = renderingContext.getFooterWidget();
        for (let i = 0; i < headerWidgets.length; i++) {
            this.setHeaderWidget(headerWidgets[i].column, headerWidgets[i].widget);
        }
        for (let i = 0; i < cellToWidget.length; i++) {
            this.setWidget(cellToWidget[i].row, cellToWidget[i].column, cellToWidget[i].widget);
        }
        // FIX
        for (let i = 0; i < footerWidgets.length; i++) {
            this.setFooterWidget(footerWidgets[i].row, footerWidgets[i].column, footerWidgets[i].widget);
        }
        this.updateHeaderDataDisplayTypes(renderingContext);
        this.gridLayout.postRender();
    }
    renderBody() {
        // Don't observe while we rebuild the table
        let renderingContext = new RenderingContext_1.default();
        this.table.removeChild(this.tbody);
        const nearestTableAncestor = this.findNearestTableAncestor(this.tfoot);
        if (nearestTableAncestor === this.table) {
            this.table.removeChild(this.tfoot);
        }
        const tr = this.thead.firstElementChild;
        if (tr) {
            Array.from(tr.children).forEach((th, columnIndex) => {
                if (this.headerStyles) {
                    th.classList.add(...this.headerStyles);
                }
                const headerColumnStyle = this.columnStyles[columnIndex];
                if (headerColumnStyle && headerColumnStyle.length > 0) {
                    th.classList.add(...headerColumnStyle);
                }
                if (th.getAttribute(fmko_ts_common_1.ARIA.SORT)) {
                    th.removeAttribute(fmko_ts_common_1.ARIA.SORT);
                }
                if (this.currentlySortedHeader?.column === columnIndex) {
                    th.setAttribute(fmko_ts_common_1.ARIA.SORT, this.currentlySortedHeader.sortDirection);
                }
            });
        }
        this.tbody = this.renderRows(renderingContext);
        this.tfoot = this.renderFooters(renderingContext);
        this.table.appendChild(this.tbody);
        if (Object.keys(this.columnFooters).length) {
            this.table.appendChild(this.tfoot);
        }
        const cellToWidget = renderingContext.getCell2Widget();
        const footerWidgets = renderingContext.getFooterWidget();
        for (let i = 0; i < cellToWidget.length; i++) {
            this.setWidget(cellToWidget[i].row, cellToWidget[i].column, cellToWidget[i].widget);
        }
        // FIX
        for (let i = 0; i < footerWidgets.length; i++) {
            this.setFooterWidget(footerWidgets[i].row, footerWidgets[i].column, footerWidgets[i].widget);
        }
        this.updateHeaderDataDisplayTypes(renderingContext);
        this.gridLayout.postRender();
    }
    getRowCount() {
        if (this.tbody) {
            return fmko_ts_common_1.DOM.getChildCount(this.tbody);
        }
        return 0;
    }
    clearHTMLChildren() {
        if (this.table) {
            while (this.table.firstChild) {
                this.table.removeChild(this.table.firstChild);
            }
        }
    }
    removeRow(row) {
        const tr = this.getElement(row);
        if (tr) {
            this.tbody.removeChild(tr);
        }
    }
    getElementForEvent(event) {
        let element = event.target;
        const nearestTableElement = this.findNearestTableAncestor(element);
        if (nearestTableElement === this.table) {
            return element;
        }
        return null;
    }
    getRowForEvent(event) {
        let td = this.getEventTargetCell(event);
        if (!td) {
            return null;
        }
        return td.parentElement.sectionRowIndex;
    }
    /**
     * Determines the TD associated with the specified event.
     *
     * @param event
     *            the event to be queried
     * @return the TD associated with the event, or <code>null</code> if none is
     *         found.
     */
    getEventTargetCell(event) {
        let td = event.target;
        for (; td; td = td.parentElement) {
            // If it"s a TD, it might be the one we"re looking for.
            if (td instanceof HTMLTableCellElement) {
                // Make sure it"s directly a part of this table before returning
                // it.
                let tr = td.parentElement;
                let body = tr.parentElement;
                if (body === this.tbody) {
                    return td;
                }
            }
            // If we run into this table"s body, we"re out of options.
            if (td === this.tbody) {
                return null;
            }
        }
        return null;
    }
    getElement(row, column) {
        if (!this.tbody) {
            return null;
        }
        let tr = fmko_ts_common_1.DOM.getNthChild(this.tbody, row);
        if (tr && column != null) {
            return fmko_ts_common_1.DOM.getNthChild(tr, column);
        }
        else {
            return tr;
        }
    }
    getTrFromDataRow(row) {
        if (!this.tbody) {
            return null;
        }
        return Array.from(this.tbody.children)
            .find(child => child.getAttribute(GridConstants_1.default.GRID_ATTRIBUTE_DATAROW) === `${row}`);
    }
    insertRow(beforeRow) {
        const tr = fmko_ts_common_1.DOM.createTR();
        fmko_ts_common_1.DOM.insertChild(this.tbody, tr, beforeRow);
        return tr;
    }
    setHeaderWidget(cellIndex, widget) {
        let td = this.ensureCell({
            row: 0, cellIndex,
            ensureOn: this.thead
        });
        if (widget) {
            this.add(widget);
            td.appendChild(widget.element);
        }
    }
    setWidget(row, cellIndex, widget) {
        let td = this.ensureCell({
            row,
            cellIndex
        });
        if (widget) {
            this.add(widget);
            td.appendChild(widget.element);
        }
    }
    setFooterWidget(row, column, widget) {
        const tr = this.tfoot.querySelectorAll("tr")[row];
        const td = tr.querySelectorAll("td")[column];
        if (widget) {
            this.add(widget);
            td.appendChild(widget.element);
        }
    }
    getWidget(tr, column) {
        if (tr) {
            let td = fmko_ts_common_1.DOM.getNthChild(tr, column);
            if (td) {
                let ch = this.getChildren;
                for (let i = 0; i < ch.length; i++) {
                    if (ch[i].getElement().getParentElement() === td) {
                        return ch[i];
                    }
                }
            }
        }
        return null;
    }
    getCellWidget(td) {
        if (!td) {
            return;
        }
        const children = this.getChildren();
        for (const child of children) {
            if (child instanceof fmko_ts_common_1.Widget && child.element.parentElement === td) {
                return child;
            }
        }
    }
    getTBody() {
        return this.tbody;
    }
    addHeaderStyle(...tokens) {
        this.headerStyles = tokens;
    }
    addFooterStyle(...tokens) {
        this.footerStyles = tokens;
    }
    setColSpan(row, cellIndex, numColumns) {
        let td = this.ensureCell({
            row,
            cellIndex
        });
        td.colSpan = numColumns;
        return td;
    }
    ensureCell({ row, cellIndex, ensureOn }) {
        const element = ensureOn || this.tbody;
        let tr = fmko_ts_common_1.DOM.getNthChild(element, row);
        let td = fmko_ts_common_1.DOM.getNthChild(tr, cellIndex);
        if (!td) {
            td = document.createElement("td");
            // Add attributes
            fmko_ts_common_1.DOM.insertChild(tr, td, cellIndex);
        }
        return td;
    }
    ensureHeaderFilter(td, columnConfigIndex) {
        const header = this.columnHeaders[columnConfigIndex];
        const headerText = typeof header === "string" ? header : "";
        const currentFilterHeader = this.filterHeaders.find(filterHeader => filterHeader.header === headerText);
        if (headerText) {
            td.dataset.headerText = headerText;
            if (currentFilterHeader) {
                td.dataset.columnFilterId = `${headerText}`;
                (0, fmko_ts_common_1.setElementVisible)(td, currentFilterHeader.visible);
            }
        }
    }
    setRowStyle(row, style) {
        let tr = fmko_ts_common_1.DOM.getNthChild(this.tbody, row);
        tr.className = style;
    }
    removeRowStyle(row, style) {
        let tr = fmko_ts_common_1.DOM.getNthChild(this.tbody, row);
        tr.classList.remove(style);
    }
    getRowStyle(row) {
        if (!this.tbody) {
            return "";
        }
        let tr = fmko_ts_common_1.DOM.getNthChild(this.tbody, row);
        return tr.className;
    }
    addRowStyle(row, style) {
        let tr = fmko_ts_common_1.DOM.getNthChild(this.tbody, row);
        tr.classList.add(style);
    }
    setColumnStyle(row, cellIndex, style) {
        let td = this.ensureCell({
            row,
            cellIndex
        });
        td.className = style;
    }
    addTableClasses(...classes) {
        this.table.classList.add(...classes);
    }
    removeTableCssClass(tableClass) {
        this.table.classList.remove(tableClass);
    }
    setStyleName(...classes) {
        const newClasses = this.ensureClassList(...classes);
        this.table.className = "";
        this.table.classList.add(...newClasses);
    }
    removeStyleNames() {
        this.table.className = "";
    }
    addStyleName(...classes) {
        const ensuredClassList = this.ensureClassList(...classes);
        if (ensuredClassList.length) {
            this.table.classList.add(...ensuredClassList);
        }
        else {
            this.removeStyleNames();
        }
    }
    setHtmlHeader(column, headerText) {
        this.columnHeaders[column] = headerText;
    }
    getTableElement() {
        return this.table;
    }
    getHeaderElement(column) {
        return this.thead.children.item(0).childNodes.item(column);
    }
    addFixedRowStyle(...classes) {
        const ensuredClassList = this.ensureClassList(...classes);
        this.fixedRowStyle.push(...ensuredClassList);
    }
    findNearestTableAncestor(element) {
        let relevantElement = element;
        while (relevantElement.parentElement) {
            relevantElement = relevantElement.parentElement;
            if (relevantElement instanceof HTMLTableElement) {
                return relevantElement;
            }
        }
        return null;
    }
    ensureClassList(...classes) {
        let newArray = classes;
        if (classes.length === 1) {
            const [firstStyle] = classes;
            if (firstStyle) {
                newArray = firstStyle.split(" ").filter(currentStyle => !!currentStyle);
            }
            else {
                newArray = [];
            }
        }
        else if (classes.length === 0) {
            newArray = [];
        }
        return newArray;
    }
    /**
     * Render methods
     */
    renderTable() {
        const table = document.createElement("table");
        table.setAttribute("cellpadding", "0");
        table.setAttribute("cellspacing", "0");
        table.setAttribute("border", "0");
        table.setAttribute("summary", this.summary);
        const caption = document.createElement("caption");
        caption.textContent = this.caption;
        table.appendChild(caption);
        return table;
    }
    renderHeader(renderingContext) {
        const thead = document.createElement("thead");
        const tr = document.createElement("tr");
        thead.appendChild(tr);
        if (this.headerStyles) {
            thead.classList.add(...this.headerStyles);
        }
        this.columnHeaders.forEach((header, columnIndex) => {
            const th = document.createElement("th");
            const thSpan = document.createElement("span");
            th.appendChild(thSpan);
            renderingContext.column = columnIndex;
            if (header instanceof fmko_ts_common_1.Widget) {
                renderingContext.setHeaderWidget(header);
            }
            const headerColumnStyle = this.columnStyles[columnIndex];
            let columnString = "";
            if (header instanceof HiddenTableHeader_1.default) {
                columnString = header.options.headerName;
            }
            else if (typeof header === "string") {
                columnString = header;
            }
            thSpan.textContent = `${columnString}`;
            thSpan.dataset.boldedText = `${columnString}`;
            if (header instanceof HiddenTableHeader_1.default) {
                thSpan.classList.add("sr-only");
            }
            const currentFilterHeader = this.filterHeaders.find(filterHeader => filterHeader.header === header);
            if (currentFilterHeader) {
                th.dataset.columnFilterId = `${columnString}`;
            }
            if (headerColumnStyle?.length > 0) {
                th.classList.add(...headerColumnStyle);
                if (headerColumnStyle.includes(GridConstants_1.default.GRID_COLUMN_SORTABLE)) {
                    thSpan.classList.add("flex-row", "align-items-center", "quarter-gap");
                    const sortIcon = new fmko_ts_widgets_1.ImageWidget({
                        src: fmko_ts_common_1.ImageSrcWithVersion.getImage(fmko_ts_common_1.ImageSrc.ASCENDING),
                        alt: "",
                        imageDimension: fmko_ts_widgets_1.ImageDimensions.M_NO_CROP
                    });
                    thSpan.appendChild(sortIcon.element);
                    th.addEventListener("sort", () => {
                        thSpan.dispatchEvent(new Event("sort"));
                    });
                    fmko_ts_common_1.ToggleTip.createToggleTip(thSpan, async () => {
                        const isSorted = th.hasAttribute(fmko_ts_common_1.ARIA.SORT);
                        if (!isSorted) {
                            return;
                        }
                        const isAscending = th.getAttribute(fmko_ts_common_1.ARIA.SORT) === SortDirection_1.SortDirection.ASCENDING;
                        const isDateColumn = th.classList.contains(GridColumnDataDisplayedType_1.GridColumnDataDisplayedType.DATE);
                        if (isDateColumn) {
                            return {
                                header: isAscending
                                    ? "Sorteret efter: Ældste øverst (Tid)"
                                    : "Sorteret efter: Nyeste øverst (Tid)"
                            };
                        }
                        const isNumberColumn = th.classList.contains(GridColumnDataDisplayedType_1.GridColumnDataDisplayedType.NUMBER);
                        if (isNumberColumn) {
                            return {
                                header: isAscending
                                    ? "Sorteret efter: Mindste øverst (Nummerisk)"
                                    : "Sorteret efter: Største øverst (Nummerisk)"
                            };
                        }
                        // const isStringColumn = th.classList.contains(GridColumnDataDisplayedType.STRING);
                        return {
                            header: isAscending
                                ? "Sorteret efter: A-Z (Alfabetisk)"
                                : "Sorteret efter: Z-A (Omvendt alfabetisk)"
                        };
                    }, {
                        focusEffect: false,
                        hoverEffect: false,
                        buttonBackgroundColor: fmko_ts_common_1.ButtonBackgroundColor.NO_BG,
                        associatedElementEvent: {
                            eventType: "sort",
                            reRenderOnAssociatedElementEvent: true
                        }
                    });
                }
            }
            tr.appendChild(th);
            if (currentFilterHeader) {
                (0, fmko_ts_common_1.setElementVisible)(th, currentFilterHeader.visible);
            }
            if (th.getAttribute(fmko_ts_common_1.ARIA.SORT)) {
                th.removeAttribute(fmko_ts_common_1.ARIA.SORT);
            }
            if (this.currentlySortedHeader?.column === columnIndex) {
                th.setAttribute(fmko_ts_common_1.ARIA.SORT, this.currentlySortedHeader.sortDirection);
            }
        });
        return thead;
    }
    renderRows(renderingContext) {
        const tbody = document.createElement("tbody");
        const arrayOfNumbers = Array.from({ length: this.gridLayout.numInitialDomRows() }, (_, index) => index);
        for (const rowNumber of arrayOfNumbers) {
            const tr = document.createElement("tr");
            renderingContext.row = rowNumber;
            const rowStyle = this.gridLayout.getRowStyle(rowNumber);
            const renderedRow = [...rowStyle, ...this.fixedRowStyle];
            if (renderedRow?.length > 0) {
                tr.classList.add(...renderedRow);
            }
            const rowAttributes = this.gridLayout.getRowAttributes(rowNumber);
            if (rowAttributes && rowAttributes.length > 0) {
                const actualAttributes = rowAttributes.split(" ").map(attribute => {
                    const [key, value] = attribute.split("=");
                    return [key, value.substring(1, value.length - 1)]; // remove " from start and end
                });
                for (const [key, value] of actualAttributes) {
                    tr.setAttribute(key, value);
                }
            }
            let colSpan;
            let cellIndex = 0;
            tbody.appendChild(tr);
            for (let columnIndex = 0; columnIndex < this.columnHeaders.length; columnIndex += colSpan) {
                renderingContext.column = cellIndex;
                const columnStyle = this.gridLayout.getCellStyle(rowNumber, columnIndex);
                colSpan = this.gridLayout.getColSpan(rowNumber, columnIndex);
                const td = this.ensureCell({
                    row: rowNumber,
                    cellIndex: cellIndex,
                    ensureOn: tbody
                });
                this.ensureHeaderFilter(td, columnIndex);
                renderingContext.currentCell = td;
                if (colSpan > 0) {
                    if (columnStyle?.length > 0) {
                        td.classList.add(...columnStyle);
                    }
                    if (colSpan !== 1) {
                        const hiddenHeaders = this.filterHeaders.filter(filterHeader => {
                            if (filterHeader.visible) {
                                return false;
                            }
                            return filterHeader.columnIndex > columnIndex && filterHeader.columnIndex <= columnIndex + colSpan;
                        }).length;
                        td.setAttribute("colSpan", `${colSpan - hiddenHeaders}`);
                    }
                    this.gridLayout.renderCell(td, renderingContext, rowNumber, columnIndex);
                    td.innerHTML = fmko_ts_common_1.HTMLUtils.sanitizeHTML(renderingContext.toString());
                    td.dataset.boldedText = renderingContext.toString();
                    renderingContext.resetHTML();
                    tr.appendChild(td);
                }
                cellIndex++;
            }
        }
        return tbody;
    }
    renderFooters(renderingContext) {
        const tfoot = document.createElement("tfoot");
        Object.entries(this.columnFooters).forEach(([rowNumber, row]) => {
            const tr = document.createElement("tr");
            if (this.footerStyles) {
                tr.classList.add(...this.footerStyles);
            }
            tfoot.appendChild(tr);
            row.forEach((footerWrapper, columnIndex) => {
                const td = document.createElement("td");
                renderingContext.column = columnIndex + 1;
                const columnString = footerWrapper.footer instanceof fmko_ts_common_1.Widget ? "" : footerWrapper.footer;
                if (footerWrapper.footer instanceof fmko_ts_common_1.Widget) {
                    const rowAsNumber = Number(rowNumber);
                    renderingContext.setFooterWidget(footerWrapper.footer, rowAsNumber, columnIndex);
                }
                const headerColumnStyle = this.columnStyles[columnIndex];
                if (headerColumnStyle && headerColumnStyle.length > 0) {
                    td.classList.add(...this.columnStyles[columnIndex]);
                }
                if (footerWrapper.footerClasses) {
                    td.classList.add(...footerWrapper.footerClasses);
                }
                if (footerWrapper.footerStyle) {
                    Object.assign(td.style, footerWrapper.footerStyle);
                }
                td.textContent = `${columnString}`;
                tr.appendChild(td);
            });
        });
        return tfoot;
    }
    updateHeaderDataDisplayTypes(renderingContext) {
        for (let i = 0; i < this.columnHeaders.length; i++) {
            const dataDisplayType = renderingContext.getColumnDataDisplayType(i);
            const th = this.getHeaderElement(i);
            th?.classList?.add(dataDisplayType);
        }
    }
}
exports.default = ImprovedHTMLTable;
//# sourceMappingURL=ImprovedHTMLTable.js.map